import { Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  CustomModalService,
  emailRegex,
  FileUploadComponent,
  FileUploadConfig,
  FileUploadResult,
} from '@piqet-angular/shared';
import { MyOrganizationService } from 'libs/data/src/lib/services/my-organization/my-organization.service';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import * as XLSX from 'xlsx';

// User interface for imported data
interface ImportedUser {
  firstName: string;
  lastName: string;
  email: string;
  countryName: string;
  stateName: string;
  address: string;
  phone: string;
  postCode: string;
}

interface ValidationError {
  row: number;
  field: string;
  message: string;
}

@Component({
  selector: 'piqet-angular-import-users',
  templateUrl: './import-users.component.html',
  styleUrls: ['./import-users.component.scss'],
})
export class ImportUsersComponent implements OnInit {
  @ViewChild(FileUploadComponent) fileUploadComponent!: FileUploadComponent;

  importUserForm: FormGroup;
  roleList$!: Observable<any[]>;

  // File upload related properties
  selectedFile: File | null = null;
  isUploading = false;

  // Excel data
  parsedUsers: ImportedUser[] = [];
  validationErrors: ValidationError[] = [];
  fileName: string = '';

  // Constants
  readonly MAX_USERS = 100;

  // Excel upload configuration
  excelUploadConfig: Partial<FileUploadConfig> = {
    showPreview: false,
    placeholderText: 'Select excel file with user data',
    selectButtonText: 'Choose excel',
    changeButtonText: 'Change',
    removeButtonText: 'Remove',
    customErrorMessage: 'Please select a valid Excel file (.xls, .xlsx, .csv) - Max 15MB',
  };
  serverErrorMessage = '';
  userLicenseInfo: any;
  @Output() uploadImportUserEvent = new EventEmitter<any>();

  constructor(
    private modal: CustomModalService,
    private fb: FormBuilder,
    private _manageOrgService: MyOrganizationService,
    private toaster: ToastrService
  ) {
    this.importUserForm = this.fb.group({
      role: [null, Validators.required],
      excelFile: [null, Validators.required],
    });
  }

  ngOnInit(): void {
    // Transform the API response to extract only the data array
    this.roleList$ = this._manageOrgService
      .getAllRoleList()
      .pipe(map(response => response?.data || []));
    this.userLicenseInfo = this.modal?.modalService?.config?.initialState?.['userLicenseInfo'];
  }

  getLicenseInfoMessage(): string {
    if (!this.userLicenseInfo) {
      return '';
    }
    const { leftUser, activeUser, totalUser } = this.userLicenseInfo;
    if (leftUser === 0) {
      return `All ${totalUser} user licenses are currently in use. Please upgrade your plan or contact support to add more users.`;
    }
    return `You currently have ${activeUser} of ${totalUser} user licenses in use. ${leftUser} license(s) are still available for adding new users.`;
  }

  onFileSelected(result: FileUploadResult): void {
    if (result.isValid && result.file) {
      this.selectedFile = result.file;
      this.fileName = result.fileName || '';
      this.importUserForm.patchValue({ excelFile: result.file });

      // Clear both types of errors
      this.validationErrors = [];
      this.serverErrorMessage = '';

      // Parse and validate Excel content
      this.parseExcelFile(result.file);
    } else {
      this.resetFileData();
      this.serverErrorMessage = 'Invalid file selected. Please choose a valid Excel file.';
    }
  }

  parseExcelFile(file: File): void {
    const reader = new FileReader();

    reader.onload = (e: any) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });

        // Get first sheet
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];

        // Convert to JSON with header mapping
        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: '',
        });

        if (jsonData.length < 2) {
          this.serverErrorMessage = 'Excel file is empty or has no data rows';
          return;
        }

        // Parse headers and data
        const headers = jsonData[0] as string[];
        const dataRows = jsonData.slice(1);

        // Map Excel columns to user properties
        this.parsedUsers = this.mapExcelToUsers(headers, dataRows);

        // If no users after filtering empty records
        if (this.parsedUsers.length === 0) {
          this.serverErrorMessage = 'No valid user records found in the file';
          return;
        }

        // Validate all users
        this.validateUsers();
      } catch (error) {
        console.error('Error parsing Excel file:', error);
        this.serverErrorMessage = 'Error parsing Excel file. Please check the file format.';
      }
    };

    reader.onerror = () => {
      this.serverErrorMessage = 'Error reading file. Please try again.';
    };

    reader.readAsArrayBuffer(file);
  }

  mapExcelToUsers(headers: string[], dataRows: any[]): ImportedUser[] {
    // Create a map of header names to indices (case-insensitive and remove asterisks)
    const headerMap = new Map<string, number>();
    headers.forEach((header, index) => {
      const normalizedHeader = header.toString().toLowerCase().trim().replace('*', ''); // Remove asterisks from headers
      headerMap.set(normalizedHeader, index);
    });

    // Helper function to get value by possible header names
    const getValue = (row: any[], possibleHeaders: string[]): string => {
      for (const header of possibleHeaders) {
        const index = headerMap.get(header.toLowerCase());
        if (index !== undefined && row[index] !== undefined && row[index] !== null) {
          return String(row[index]).trim(); // Always trim the value
        }
      }
      return '';
    };

    // Map each row to ImportedUser object
    return dataRows
      .map((row, rowIndex) => {
        // Skip empty rows
        if (!row || row.length === 0 || row.every((cell: any) => !cell || cell === '')) {
          return null;
        }

        const user = {
          firstName: getValue(row, ['first name', 'firstname', 'first_name', 'fname']),
          lastName: getValue(row, ['last name', 'lastname', 'last_name', 'lname']),
          email: getValue(row, ['email', 'email address', 'e-mail', 'emailaddress']),
          countryName: getValue(row, ['country', 'countryname', 'country name', 'country_name']),
          stateName: getValue(row, ['state', 'statename', 'state name', 'state_name', 'province']),
          address: getValue(row, ['address', 'street', 'street address', 'address1']),
          phone: getValue(row, ['phone', 'phone number', 'phonenumber', 'mobile', 'contact']),
          postCode: getValue(row, [
            'postcode',
            'post code',
            'postal code',
            'postalcode',
            'zip',
            'zipcode',
            'zip code',
          ]),
        };

        // Check if all three required fields are empty after trimming
        const hasRequiredFields = user.firstName || user.lastName || user.email;

        // If all required fields are empty, ignore this record
        if (!hasRequiredFields) {
          return null;
        }

        return user;
      })
      .filter(user => user !== null) as ImportedUser[];
  }

  validateUsers(): void {
    this.validationErrors = [];
    this.serverErrorMessage = '';
    const emailMap = new Map<string, number[]>();

    // First check for general validation errors
    if (this.parsedUsers.length > this.MAX_USERS) {
      this.serverErrorMessage = `Maximum ${this.MAX_USERS} users allowed. Found ${this.parsedUsers.length} users in the file.`;
      this.parsedUsers = [];
      return;
    }

    // Check license limit validation
    if (this.userLicenseInfo && this.parsedUsers.length > this.userLicenseInfo.leftUser) {
      this.serverErrorMessage = `You can only add ${this.userLicenseInfo.leftUser} more users based on your license limit. Found ${this.parsedUsers.length} users in the file.`;
      this.parsedUsers = [];
      return;
    }

    // If no general errors, proceed with individual field validation
    this.parsedUsers.forEach((user, index) => {
      const rowNumber = index + 3;

      // Validate firstName (trim and check)
      const trimmedFirstName = user.firstName ? user.firstName.trim() : '';
      if (!trimmedFirstName || trimmedFirstName.length === 0) {
        this.validationErrors.push({
          row: rowNumber,
          field: 'First Name',
          message: 'First name is required',
        });
      } else if (trimmedFirstName.length < 2) {
        this.validationErrors.push({
          row: rowNumber,
          field: 'First Name',
          message: 'First name must be at least 2 characters',
        });
      }

      // Validate lastName (trim and check)
      const trimmedLastName = user.lastName ? user.lastName.trim() : '';
      if (!trimmedLastName || trimmedLastName.length === 0) {
        this.validationErrors.push({
          row: rowNumber,
          field: 'Last Name',
          message: 'Last name is required',
        });
      } else if (trimmedLastName.length < 2) {
        this.validationErrors.push({
          row: rowNumber,
          field: 'Last Name',
          message: 'Last name must be at least 2 characters',
        });
      }

      // Validate email (trim and check)
      const trimmedEmail = user.email ? user.email.trim() : '';
      if (!trimmedEmail || trimmedEmail.length === 0) {
        this.validationErrors.push({
          row: rowNumber,
          field: 'Email',
          message: 'Email is required',
        });
      } else if (!this.isValidEmail(trimmedEmail)) {
        this.validationErrors.push({
          row: rowNumber,
          field: 'Email',
          message: 'Invalid email format',
        });
      } else {
        // Check for duplicate emails
        const emailLower = trimmedEmail.toLowerCase();
        if (emailMap.has(emailLower)) {
          emailMap.get(emailLower)!.push(rowNumber);
        } else {
          emailMap.set(emailLower, [rowNumber]);
        }
      }

      // Optional: Validate phone format
      if (user.phone && user.phone.trim() && !this.isValidPhone(user.phone.trim())) {
        this.validationErrors.push({
          row: rowNumber,
          field: 'Phone',
          message: 'Invalid phone number format',
        });
      }
    });

    // Add duplicate email errors
    emailMap.forEach((rows, email) => {
      if (rows.length > 1) {
        rows.forEach(row => {
          this.validationErrors.push({
            row: row,
            field: 'Email',
            message: `Duplicate email found. Email "${email}" appears in rows: ${rows.join(', ')}`,
          });
        });
      }
    });

    // Sort errors by row number for better readability
    this.validationErrors.sort((a, b) => {
      if (a.row === b.row) {
        return a.field.localeCompare(b.field);
      }
      return a.row - b.row;
    });
  }

  isValidEmail(email: string): boolean {
    const regex = new RegExp(emailRegex);
    return regex.test(email.trim());
  }

  isValidPhone(phone: string): boolean {
    // Remove all non-digits
    const digitsOnly = phone.replace(/\D/g, '');
    // Check if it's between 10-15 digits (international format)
    return digitsOnly.length >= 10 && digitsOnly.length <= 15;
  }

  onFileRemoved(): void {
    this.resetFileData();
  }

  resetFileData(): void {
    this.selectedFile = null;
    this.fileName = '';
    this.parsedUsers = [];
    this.validationErrors = [];
    this.serverErrorMessage = '';
    this.importUserForm.patchValue({ excelFile: null });
  }

  onRemoveConfirmation(): void {
    this.fileUploadComponent?.reset();
    this.onFileRemoved();
  }

  downloadTemplate(): void {
    const link = document.createElement('a');
    link.href = '/assets/UserImportTemplate.xlsx';
    link.download = 'UserImportTemplate.xlsx';
    link.click();
  }

  onClose(): void {
    // Reset form and close modal
    this.importUserForm.reset();
    this.resetFileData();
    this.serverErrorMessage = '';
    this.modal.close();
  }

  onSave(): void {
    if (this.importUserForm.valid && this.selectedFile) {
      // Check if there are any errors (validation or general)
      if (this.validationErrors.length > 0 || this.serverErrorMessage) {
        return;
      }

      this.isUploading = true;

      // Prepare data to send
      const importData = {
        fileName: this.fileName,
        roleId: this.importUserForm.get('role')?.value,
        userList: this.parsedUsers,
      };

      // Send data to server
      this.importUsers(importData);
    } else {
      // Mark all fields as touched to show validation errors
      Object.keys(this.importUserForm.controls).forEach(key => {
        this.importUserForm.get(key)?.markAsTouched();
      });
    }
  }

  private importUsers(importData: any): void {
    // Send JSON data
    this._manageOrgService.importUsers(importData).subscribe(
      response => {
        this.isUploading = false;

        // Check messageType to determine if it's success (0) or error
        if (response.messageType === 0) {
          this.uploadImportUserEvent.emit(response);
          this.modal.close();
        } else {
          // Server returned an error - show as general notification
          this.serverErrorMessage = response.message || 'An error occurred during import';
          this.validationErrors = [];
        }
      },
      error => {
        this.isUploading = false;

        // Handle different error scenarios - show as general notification
        let errorMessage = 'Failed to import users. Please try again.';
        if (error.status === 400) {
          errorMessage = error.error?.message || 'Invalid file format or data';
        } else if (error.status === 413) {
          errorMessage = 'File size exceeds the maximum limit';
        }

        this.serverErrorMessage = errorMessage;
        // Clear validation errors table when showing server errors
        this.validationErrors = [];

        console.error('Import error:', error);
      }
    );
  }

  copyPassword(password: string): void {
    navigator.clipboard.writeText(password).then(() => {
      this.toaster.success('Password copied successfully!');
    });
  }
}
